import { ok, err } from 'neverthrow'
import { TCommandParamsIfExists, TReturnTypeOfCommandIfExists }
  from '../core/interfaces/command/CommandTypeUtils'
import { makeAsyncExecutableCommand } from '../core/commands/makeAsyncExecutableCommand'
import { AsyncExecutableCmd } from '../core/interfaces/command/AsyncExecutableCmd'
import { AsyncCmdResponse } from '../core/interfaces/command/AsyncCmdResponse'
import { AsyncCommandSender } from '../core/interfaces/sender/AsyncCommandSender'
import * as Ping from '../core/ping/ping'

/**
 * Utility type to extract the error type(s) form an AsyncSender
 * see https://stackoverflow.com/questions/44851268/typescript-how-to-extract-the-generic-parameter-from-a-type
 */
type extractErrorFromGeneric<Type> = Type extends AsyncCommandSender<unknown, infer E> ? E : never

/**
 * Abstract base command sender class.
 * Use this class internally to build the library's senders
 * from (e.g. 'JsonRpcSender').
 */
export abstract class TypedAsyncCommandApi<
  T,
  S extends AsyncCommandSender<unknown, E>,
  E = extractErrorFromGeneric<S>,
  > {
  public readonly commandSender: S;

  public readonly pingFn: Ping.PingFunction

  constructor(commandSender: S, pingFn: Ping.PingFunction = Ping.noPing) {
    this.commandSender = commandSender
    this.pingFn = pingFn
  }

  protected makeCmd<K extends keyof T>(
    commandName: K,
    params: TCommandParamsIfExists<T, K>,
  ) {
    const payload = {
      commandName: commandName as string,
      params,
    }
    // eslint-disable-next-line max-len
    return makeAsyncExecutableCommand<T, K, E>({
      command: commandName,
      params,
      execFn: async () => {
        const res = await this.commandSender.send(payload)
        if (res.isOk()) return ok(res.value as TReturnTypeOfCommandIfExists<T, K>)
        return err(res.error as E)
      },
    })
  }

  protected async sendCmd<K extends keyof T>(
    cmd: AsyncExecutableCmd<T, K, E>,
  ): AsyncCmdResponse<T, K, E> {
    const res = await cmd.exec()
    return res
  }

  public ping() {
    return this.pingFn()
  }
}
